import { Phone, Mail, ExternalLink, Trash2, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ScoreBadge } from "./ScoreBadge";
import { VERDICT_LABEL } from "@/hooks/useDispatcherScore";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useState } from "react";
import { toast } from "sonner";

export interface DispatcherAlert {
  id: string;
  load_id: string | null;
  title: string;
  message: string | null;
  metadata: Record<string, any> | null;
  is_read: boolean;
  created_at: string;
  action_url: string | null;
}

interface Props {
  alert: DispatcherAlert;
  loadDetails?: {
    contact_phone?: string | null;
    contact_email?: string | null;
    rate_amount?: number | null;
    total_miles?: number | null;
    equipment_type?: string | null;
    origin_city?: string;
    origin_state?: string;
    destination_city?: string;
    destination_state?: string;
  };
  selected: boolean;
  onSelectChange: (id: string, checked: boolean) => void;
  onDeleted: (id: string) => void;
  onToggleRead: (id: string, isRead: boolean) => void;
}

function timeAgo(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export function AlertCard({ alert, loadDetails, selected, onSelectChange, onDeleted, onToggleRead }: Props) {
  const navigate = useNavigate();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [busy, setBusy] = useState(false);

  const score: number | null = alert.metadata?.score ?? null;
  const verdict = score == null ? null : score >= 85 ? "call_now" : score >= 70 ? "worth_watching" : score >= 60 ? "negotiate_higher" : "weak_market";
  const reasons: string[] = alert.metadata?.reasons || [];
  const label: string = alert.metadata?.watch_label || "ATLAS";

  const handleDelete = async () => {
    setBusy(true);
    const { error } = await supabase.from("notifications").delete().eq("id", alert.id);
    if (error) {
      toast.error("Failed to delete alert");
      setBusy(false);
      return;
    }
    setRemoving(true);
    setConfirmOpen(false);
    toast.success("Alert deleted");
    setTimeout(() => onDeleted(alert.id), 200);
  };

  const handleToggleRead = async () => {
    const next = !alert.is_read;
    setBusy(true);
    const { error } = await supabase
      .from("notifications")
      .update({ is_read: next, read_at: next ? new Date().toISOString() : null })
      .eq("id", alert.id);
    setBusy(false);
    if (error) {
      toast.error("Failed to update alert");
      return;
    }
    onToggleRead(alert.id, next);
  };

  return (
    <>
      <div
        className={`rounded-xl border bg-card p-4 shadow-sm transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 ${
          alert.is_read ? "bg-muted/30" : ""
        } ${removing ? "opacity-0 scale-95" : "opacity-100 scale-100"}`}
      >
        <div className="flex items-start gap-3">
          <Checkbox
            checked={selected}
            onCheckedChange={(v) => onSelectChange(alert.id, !!v)}
            className="mt-1"
            aria-label="Select alert"
          />
          <ScoreBadge score={score} size="lg" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              {verdict && (
                <span className="text-[10px] font-bold tracking-wider text-brand-teal">
                  {VERDICT_LABEL[verdict]}
                </span>
              )}
              <span className="text-[10px] text-muted-foreground ml-auto">{timeAgo(alert.created_at)}</span>
            </div>
            <div className="font-semibold text-sm mt-0.5 truncate">{alert.title}</div>
            {alert.message && <div className="text-xs text-muted-foreground mt-0.5 truncate">{alert.message}</div>}
            <div className="text-[11px] text-muted-foreground mt-1 truncate">
              Watch: <strong>{label}</strong>
              {reasons.length > 0 && ` · ${reasons.join(" · ")}`}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-1.5 mt-3 pt-3 border-t">
          {loadDetails?.contact_phone && (
            <Button size="sm" variant="outline" asChild className="h-7 px-2 text-xs">
              <a href={`tel:${loadDetails.contact_phone}`}><Phone className="h-3 w-3 mr-1" />Call</a>
            </Button>
          )}
          {loadDetails?.contact_email && (
            <Button size="sm" variant="outline" asChild className="h-7 px-2 text-xs">
              <a href={`mailto:${loadDetails.contact_email}`}><Mail className="h-3 w-3 mr-1" />Email</a>
            </Button>
          )}
          {alert.load_id && (
            <Button
              size="sm"
              className="bg-brand-teal hover:bg-brand-teal/90 text-white h-7 px-2 text-xs"
              onClick={() => navigate(`/search?load=${alert.load_id}`)}
            >
              <ExternalLink className="h-3 w-3 mr-1" />Open
            </Button>
          )}
          <div className="ml-auto flex items-center gap-1">
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7 text-muted-foreground hover:text-foreground"
              onClick={handleToggleRead}
              disabled={busy}
              title={alert.is_read ? "Mark as unread" : "Mark as read"}
            >
              {alert.is_read ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
            </Button>
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7 text-muted-foreground hover:text-destructive"
              onClick={() => setConfirmOpen(true)}
              disabled={busy}
              title="Delete alert"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Alert</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to permanently delete this alert? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleDelete(); }}
              disabled={busy}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
